import { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import useAPI from 'services/useAPI';
import useApiCall from 'services/useApiCall';
import { useModelsQuery, useModelQuery, useBindingQuery } from '../queries';
/*
import { useQuery } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
*/
import {
  createBindingId,
  parseBindingId,
  parseContentId,
  toBindingContentId,
  createBindingIdFromModelId
} from '../utils/model';

import { mapModelToBindings } from './MapModelToBindings';
import { envelopeToDtdlList } from '../utils/model';
import { } from '../utils/model';
import { nanoid } from 'nanoid';

/*
const defaultBinding = ({ modelId }) => {
  let name = nanoid(8);
  const btbi = createBindingIdFromModelId(modelId, name);
  const bindingDraft = {
    id: btbi,
    assetModelId: modelId,
    sources: [],
    draft: true,
    modelData: {
      '@id': btbi,
      '@type': 'Binding Interface',
      '@context': 'btmi:btdl:context;1',
      description: '',
      displayName: btbi,
      contents: []
    }
  };
  return bindingDraft;
};
*/
const useBindingModel = ({ bindingId, bindingDraft, modelDraft, modelId, addSuccessAlert, addErrorAlert }) => {
  const api = useAPI();

  // const [modelEnvelope, setModelEnvelope] = useState(null);
  const [savedModelEnvelope, setSavedModelEnvelope] = useState();
  const [changesInModel, setChangesInModel] = useState({});
  const [modelInfo, setModelInfo] = useState(null);
  const [updatedTelemetryBindings, setUpdatedTelemetryBindings] = useState([]);
  const [updatedPropertyBindings, setUpdatedPropertyBindings] = useState([]);
  const [updatedCommandBindings, setUpdatedCommandBindings] = useState([]);

  const defaultBindingDraft = useMemo(() => {
    const name = nanoid(8);
    const btbi = createBindingIdFromModelId(modelId, name);
    const bindingDraft = {
      id: btbi,
      assetModelId: modelId,
      sources: [],
      draft: true,
      modelData: {
        '@id': btbi,
        '@type': 'Binding Interface',
        '@context': 'btmi:btdl:context;1',
        description: '',
        displayName: btbi,
        contents: []
      }
    };
    return bindingDraft;
  }, [modelId]);


  const { model: targetModel, isLoading: isTargetLoading } = useModelQuery(modelId, modelDraft);
  const { models, isLoading: isModelsLoading } = useModelsQuery({ includeDrafts: true });
  const { binding, isLoading: isBindingLoading } = useBindingQuery(bindingId, bindingDraft, defaultBindingDraft);
  const isLoading = isTargetLoading || isModelsLoading || isBindingLoading;

  const assetModels = useMemo(() => {
    return _.reject(models, model => model.id === modelId);
  }, [models, modelId]);

  const {
    commandBindings,
    propertyBindings,
    telemetryBindings
  } = useMemo(() => {
    if (isLoading) {
      return {
        commandBindings: [],
        propertyBindings: [],
        telemetryBindings: []
      };
    }

    const sourceModels = envelopeToDtdlList(_.filter(models, source => _.includes(binding?.sources, source?.id)));
    setModelInfo(oldInfo => (
      {
        ...parseBindingId(binding?.id),
        displayName: binding?.displayName,
        description: binding?.modelData?.description ?? '',
        modelId: binding?.assetModelId,
        sources: sourceModels,
        ...oldInfo
      })
    );
    const {
      telemetryBindings,
      propertyBindings,
      commandBindings
    } = mapModelToBindings(targetModel.modelData, binding.modelData, sourceModels, bindingDraft);
    return {
      commandBindings: !_.isEmpty(updatedCommandBindings) ? updatedCommandBindings : commandBindings,
      propertyBindings: !_.isEmpty(updatedPropertyBindings) ? updatedPropertyBindings : propertyBindings,
      telemetryBindings: !_.isEmpty(updatedTelemetryBindings) ? updatedTelemetryBindings : telemetryBindings
    };
  }, [binding, bindingDraft, isLoading, models, targetModel?.modelData, updatedCommandBindings, updatedPropertyBindings, updatedTelemetryBindings]);

  // console.log('modelInfo', modelInfo);
  // const [getBindingData] = useApiCall({
  //   apiCall: api.models.getBindingData,
  //   onSuccess: useCallback(({ bindingModel, overviewModels, targetEnvelope, sources }) => {
  //     setAssetModels(_.reject(overviewModels, model => model.id === targetEnvelope.dtdlModelId));
  //     const dtdlModelSources = envelopeToDtdlList(sources);
  //     setSourceModels(dtdlModelSources);
  //     const {
  //       telemetryBindings,
  //       propertyBindings,
  //       commandBindings
  //     } = mapModelToBindings(targetEnvelope.modelData, bindingModel.modelData, dtdlModelSources, bindingDraft);
  //     setModelInfo({
  //       ...parseBindingId(bindingModel.id),
  //       displayName: bindingModel.displayName,
  //       description: bindingModel.modelData?.description ?? '',
  //       modelId: bindingModel.assetModelId,
  //       sources: sources
  //     });
  //     setModelEnvelope(bindingModel);
  //     setSavedModelEnvelope(bindingModel);
  //     const mainModel = targetEnvelope.modelData[0];
  //     if (!_.isEmpty(mainModel)) {
  //       setTelemetryBindings(telemetryBindings);
  //       setPropertyBindings(propertyBindings);
  //       setCommandBindings(commandBindings);
  //     }
  //     setTargetModel(targetEnvelope);
  //   }, [bindingDraft, setAssetModels, setSourceModels]),
  //   onError: useCallback((error) => {
  //     console.error(error);
  //   }, [])
  // });

  // useEffect(() => {
  //   if (bindingId && modelDraft != null && modelId != null) {
  //     getBindingData(bindingId, bindingDraft, modelDraft, modelId);
  //   }
  // }, [bindingId, bindingDraft, getBindingData, modelDraft, modelId]);

  const serializeContent = useCallback((content, btbi) => {
    let binding;
    if (content.sourceModel && content.source) {
      const { name } = parseContentId(content.contentId);
      binding = {
        '@id': toBindingContentId(btbi, name),
        contentId: content.contentId,
        // TODO Fix for multiple sources
        endpoints: [{ endpointId: content.source.name, contentId: content.source['@id'] }],
        displayName: content.displayName ?? '',
        description: content.description ?? '',
        comment: content.comment ?? ''
      };
    }
    return binding;
  }, []);

  const serializeBindings = useCallback(({ telemetryBindings, propertyBindings, commandBindings, modelInfo }) => {
    const btbi = createBindingId(modelInfo.id, modelInfo.version);
    return {
      // '@id': `${modelInfo.id};${modelInfo.version}`,
      '@id': btbi,
      '@type': 'Binding Interface',
      '@context': 'btmi:btdl:context;1',
      ...(!_.isEmpty(modelInfo.description) && { description: `${modelInfo.description}` }),
      displayName: modelInfo.displayName,
      assetModelId: modelInfo.modelId,
      contentBindingDescriptions: [
        ..._.compact(_.map(telemetryBindings, (c) => serializeContent(c, btbi))),
        ..._.compact(_.flatten(_.map(propertyBindings, (c) => serializeContent(c, btbi)))),
        ..._.compact(_.flatten(_.map(commandBindings, (c) => serializeContent(c, btbi))))
      ]
    };
  }, [serializeContent]);

  const updateSourceModels = useCallback((sources) => {
    const sourceModels = envelopeToDtdlList(sources);
    setModelInfo(oldInfo => {
      return {
        ...oldInfo,
        sources: sourceModels
      };
    });
  }, []);

  const createBindingModel = useCallback(({ telemetryBindings, propertyBindings, commandBindings, modelInfo, modelEnvelope }) => {
    const bindings = serializeBindings({ telemetryBindings, propertyBindings, commandBindings, modelInfo });
    const sources = _.map(modelInfo.sources, source => source['@id']);
    return {
      ...modelEnvelope,
      id: createBindingId(modelInfo.id, modelInfo.version),
      // dtdlModelId: bindings['@id'],
      displayName: bindings.displayName,
      modelData: bindings,
      sources
    };
  }, [serializeBindings]);

  // useEffect(() => {
  //   if (modelInfo === null) {
  //     return;
  //   }
  //   setModelEnvelope(oldEnvelope => {
  //     const binding = createBindingModel({ telemetryBindings, propertyBindings, commandBindings, modelInfo, modelEnvelope: oldEnvelope});
  //     return binding;
  //   });
  // }, [telemetryBindings, propertyBindings, commandBindings, modelInfo, createBindingModel, savedModelEnvelope, modelId]);


  const modelEnvelope = useMemo(() => {
    if (modelInfo === null) {
      return;
    }
    const newBinding = createBindingModel({ telemetryBindings, propertyBindings, commandBindings, modelInfo, modelEnvelope: binding });
    return newBinding;
  }, [binding, commandBindings, createBindingModel, modelInfo, propertyBindings, telemetryBindings]);

  const [updateDraft, isSavingDraft] = useApiCall({
    apiCall: api.core.updateBindingDraft,
    onSuccess: useCallback(() => {
      addSuccessAlert('Saved Model');
      setSavedModelEnvelope(modelEnvelope);
    }, [addSuccessAlert, modelEnvelope]),
    onError: useCallback(() => {
      addErrorAlert('Failed to save model');
    }, [addErrorAlert])
  });

  const _updateDraft = useCallback(() => {
    const data = _.pick(modelEnvelope, ['id', 'displayName', 'modelData', 'sources', 'assetModelId', 'comment', 'modelData']);
    updateDraft({ ...data, draft: true });
  }, [modelEnvelope, updateDraft]);

  useEffect(() => {
    setChangesInModel(!_.isEqual(modelEnvelope, savedModelEnvelope));
  }, [modelEnvelope, savedModelEnvelope]);

  return {
    telemetryBindings,
    commandBindings,
    propertyBindings,
    // sourceModels,
    assetModels,
    modelInfo,
    changesInModel,
    modelEnvelope,
    isSavingDraft,
    setTelemetryBindings: setUpdatedTelemetryBindings,
    setCommandBindings: setUpdatedCommandBindings,
    setPropertyBindings: setUpdatedPropertyBindings,
    setModelInfo,
    updateSourceModels,
    updateDraft: _updateDraft,
    isLoading
  };
};

export default useBindingModel;
